import { parseISO } from "date-fns";
import { TriangleAlert } from "lucide-react";
import { MotionConfig } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import EmptyState from "@/components/ui/v2/empty-state";
import UnavailablePage from "@/components/ui/v2/unavailable-page";
import { ROUTES } from "@/constants/routes";
import { useCaptainBillingAccount } from "@/features/captain-billing/api/useCaptainBillingAccount";
import { CaptainBalanceBanner } from "@/features/captain-billing/components/CaptainBalanceBanner";
import { CaptainBillingHistory } from "@/features/captain-billing/components/history/CaptainBillingHistory";
import {
  type CaptainBillingEditor,
  CaptainBillingTiles,
  TOP_UP_TILE_ID,
} from "@/features/captain-billing/components/tiles/CaptainBillingTiles";
import { CaptainUsageCard } from "@/features/captain-billing/components/usage/CaptainUsageCard";
import { MonthSoFar } from "@/features/captain-billing/components/usage/MonthSoFar";
import { useModuleStatus } from "@/features/integrations/api/useModuleStatus";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import { useClients } from "@/lib/hooks/useClients";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { formatMonthYear } from "@/utils/dateUtils";

function AdminBreadcrumbs({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const { client } = useClients();
  return useBreadcrumbs({
    items: [
      { label: t("common.clients"), href: `/${ROUTES.clients}` },
      { label: client?.name || t("common.client"), href: `/${ROUTES.clients}/${clientId}` },
      { label: t("captainBilling.title") },
    ],
  });
}

export default function CaptainBillingPage() {
  const { t, i18n } = useTranslation();
  const { clientId: routeClientId } = useParams<{ clientId?: string }>();
  const { clientId: tokenClientId } = useAuth();
  const clientId = routeClientId ?? tokenClientId ?? "";
  const canManage = usePermissionMatrix().canEdit("captainBilling");
  const [editor, setEditor] = useState<CaptainBillingEditor | null>(null);

  const { data: captainStatus, isLoading: statusLoading } = useModuleStatus("chat", { clientId: routeClientId });
  const captainActive = captainStatus?.enabled === true;
  const { data: account, isLoading, error, refetch } = useCaptainBillingAccount(clientId, captainActive);

  if (!statusLoading && !captainStatus?.enabled) return <UnavailablePage variant="integrationDisabled" />;
  if (error?.response?.status === 404) return <UnavailablePage variant="integrationDisabled" />;

  const openTopUp = () => {
    setEditor("topUp");
    document.getElementById(TOP_UP_TILE_ID)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const month = account ? formatMonthYear(parseISO(account.month), i18n.language) : "";
  const subtitle = t(`captainBilling.subtitle.${canManage ? "admin" : "manager"}`, { month });

  return (
    <ModernPageLayout
      breadcrumbs={routeClientId && <AdminBreadcrumbs clientId={routeClientId} />}
      title={t("captainBilling.title")}
      subtitle={account ? subtitle : " "}
    >
      <MotionConfig reducedMotion="user">
        <div className="flex flex-col gap-8">
          {error ? (
            <EmptyState
              icon={TriangleAlert}
              title={t("captainBilling.loadError.title")}
              description={t("captainBilling.loadError.description")}
              action={{ label: t("common.retry"), onClick: () => void refetch() }}
            />
          ) : (
            <>
              {account && <CaptainBalanceBanner account={account} onTopUp={canManage ? openTopUp : undefined} />}
              <CaptainBillingTiles
                account={account}
                isLoading={isLoading || statusLoading}
                canManage={canManage}
                editor={editor}
                onEditorChange={setEditor}
              />
              {captainActive && (
                <>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                      <CaptainUsageCard clientId={clientId} currentMonth={account?.month} />
                    </div>
                    {account && <MonthSoFar account={account} />}
                  </div>
                  <CaptainBillingHistory clientId={clientId} canManage={canManage} />
                </>
              )}
            </>
          )}
        </div>
      </MotionConfig>
    </ModernPageLayout>
  );
}
